import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';
import TeachersModule from './TeachersModule';
import StudentsModule from './StudentsModule';
import './SchoolDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const AccreditedSchoolDashboard = () => {
  const { t, i18n } = useTranslation('landing');
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('cards'); // 'cards' = only cards; section id = dedicated page

  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) {
      navigate('/business/signup/accredited', { replace: true });
      return;
    }

    fetch(`${API_BASE_URL}/api/schools/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        if (res.status === 401) {
          navigate('/business/signup/accredited', { replace: true });
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data && data.success && data.school) {
          if (data.school.schoolType !== 'accredited') {
            navigate('/business/dashboard/support', { replace: true });
            return;
          }
          setSchool(data.school);
        }
      })
      .catch(() => navigate('/business/signup/accredited', { replace: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    navigate('/business');
  };

  const sections = [
    { id: 'overview', labelKey: 'business.schoolDashboard.adminOverview', icon: 'badge', group: 'primary' },
    { id: 'teachers', labelKey: 'business.schoolDashboard.teachers', icon: 'record_voice_over', group: 'primary' },
    { id: 'classes', labelKey: 'business.schoolDashboard.classes', icon: 'class', group: 'primary' },
    { id: 'students', labelKey: 'business.schoolDashboard.studentRecords', icon: 'people', group: 'primary' },
    { id: 'finance', labelKey: 'business.schoolDashboard.financeAdmin', icon: 'payments', group: 'secondary' },
    { id: 'reports', labelKey: 'business.schoolDashboard.reportsAnalytics', icon: 'assessment', group: 'secondary' },
    { id: 'exams', labelKey: 'business.schoolDashboard.exams', icon: 'science', group: 'secondary' },
    { id: 'scoring', labelKey: 'business.schoolDashboard.scoring', icon: 'trending_up', group: 'secondary' },
    { id: 'profile', labelKey: 'business.schoolDashboard.schoolProfile', icon: 'account_balance', group: 'primary' },
    { id: 'maintenance', labelKey: 'business.schoolDashboard.maintenance', icon: 'build', group: 'tertiary' },
    { id: 'settings', labelKey: 'business.schoolDashboard.settings', icon: 'settings', group: 'tertiary' }
  ];

  if (loading) {
    return (
      <div className="school-dashboard accredited-dashboard-hub" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="school-dashboard-loading">
          <span className="material-icons spin">refresh</span>
          <p>{t('business.schoolDashboard.loading')}</p>
        </div>
      </div>
    );
  }

  if (!school) return null;

  // ——— Cards-only view: large centered cards, nothing else ———
  if (view === 'cards') {
    return (
      <div className="school-dashboard accredited-dashboard-hub" dir={isRTL ? 'rtl' : 'ltr'}>
        <header className="accredited-hub-header">
          <div className="accredited-hub-brand">
            <span className="material-icons">school</span>
            <span>{t('business.schoolDashboard.syllera')}</span>
          </div>
          <div className="accredited-hub-school">
            <span className="accredited-hub-school-name">{school.name}</span>
            <span className="accredited-hub-badge">{t('business.schoolDashboard.accreditedSchool')}</span>
          </div>
          <div className="accredited-hub-actions">
            <LanguageSwitcher className="accredited-hub-language" />
            <Link to="/business" className="accredited-hub-link">{t('business.schoolDashboard.businessHome')}</Link>
            <button type="button" className="accredited-hub-logout" onClick={handleLogout}>
              <span className="material-icons">logout</span> {t('business.schoolDashboard.logOut')}
            </button>
          </div>
        </header>

        <main className="accredited-hub-main">
          <div className="accredited-hub-cards accredited-hub-cards-grid-10">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                className="accredited-hub-card"
                onClick={() => setView(s.id)}
              >
                <span className="material-icons accredited-hub-card-icon">{s.icon}</span>
                <span className="accredited-hub-card-label">{t(s.labelKey)}</span>
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // ——— Dedicated section page (same page, not new tab) ———
  const currentSection = sections.find((s) => s.id === view);
  return (
    <div className="school-dashboard accredited-dashboard-hub accredited-dashboard-section-view" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="accredited-hub-header">
        <div className="accredited-hub-brand">
          <span className="material-icons">school</span>
          <span>{t('business.schoolDashboard.syllera')}</span>
        </div>
        <div className="accredited-hub-school">
          <span className="accredited-hub-school-name">{school.name}</span>
          <span className="accredited-hub-badge">{t('business.schoolDashboard.accreditedSchool')}</span>
        </div>
        <div className="accredited-hub-actions">
          <LanguageSwitcher className="accredited-hub-language" />
          <button type="button" className="accredited-hub-back" onClick={() => setView('cards')}>
            <span className="material-icons">arrow_back</span> {t('business.schoolDashboard.backToDashboard')}
          </button>
          <Link to="/business" className="accredited-hub-link">{t('business.schoolDashboard.businessHome')}</Link>
          <button type="button" className="accredited-hub-logout" onClick={handleLogout}>
            <span className="material-icons">logout</span> {t('business.schoolDashboard.logOut')}
          </button>
        </div>
      </header>

      <main className="accredited-hub-main accredited-section-page">
        <h1 className="accredited-section-page-title">
          <span className="material-icons">{currentSection?.icon}</span>
          {currentSection ? t(currentSection.labelKey) : ''}
        </h1>

        {view === 'overview' && (
          <div className="school-dashboard-section">
            <div className="school-dashboard-cards">
              <div className="school-dashboard-card">
                <span className="material-icons">group</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Total Students</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">badge</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Total Teachers</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">class</span>
                <div className="school-dashboard-card-value">0</div>
                <div className="school-dashboard-card-label">Total Classes</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">percent</span>
                <div className="school-dashboard-card-value">—</div>
                <div className="school-dashboard-card-label">Daily Attendance %</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">attach_money</span>
                <div className="school-dashboard-card-value">—</div>
                <div className="school-dashboard-card-label">Monthly Revenue</div>
              </div>
              <div className="school-dashboard-card">
                <span className="material-icons">trending_up</span>
                <div className="school-dashboard-card-value">—</div>
                <div className="school-dashboard-card-label">Academic Performance Index</div>
              </div>
            </div>
            <p className="school-dashboard-placeholder">Teachers, classes, and analytics will appear here once you add them.</p>
          </div>
        )}

        {view === 'teachers' && (
          <div className="school-dashboard-section school-dashboard-section-teachers">
            <TeachersModule school={school} />
          </div>
        )}

        {view === 'classes' && (
          <div className="school-dashboard-section">
            <p className="school-dashboard-placeholder">Create classes (1AP → 3AS), assign students and teachers, timetable management. This section will be implemented next.</p>
          </div>
        )}

        {view === 'students' && (
          <div className="school-dashboard-section school-dashboard-section-students">
            <StudentsModule school={school} />
          </div>
        )}

        {view === 'finance' && (
          <div className="school-dashboard-section">
            <p className="school-dashboard-placeholder">Tuition tracking, installments, salary management, expense tracking, annual financial report. This section will be implemented next.</p>
          </div>
        )}

        {view === 'reports' && (
          <div className="school-dashboard-section">
            <p className="school-dashboard-placeholder">Student & class performance, guidance and supervisor reports, graphs and charts, printable reports. This section will be implemented next.</p>
          </div>
        )}

        {view === 'exams' && (
          <div className="school-dashboard-section">
            <p className="school-dashboard-placeholder">Schedule exams, assign students, enter scores, generate reports, print exam sheets. This section will be implemented next.</p>
          </div>
        )}

        {view === 'scoring' && (
          <div className="school-dashboard-section">
            <p className="school-dashboard-placeholder">Class and student ranking, teacher scoring, overall KPI analysis, progress reports. This section will be implemented next.</p>
          </div>
        )}

        {view === 'profile' && (
          <div className="school-dashboard-section school-profile-section">
            <h2 className="school-profile-section-title">{t('business.schoolDashboard.profileForm.sectionTitle')}</h2>
            <form className="school-profile-form" onSubmit={(e) => e.preventDefault()}>
              <div className="school-profile-grid">
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.institutionName')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.institutionNamePlaceholder')} defaultValue={school?.name} />
                </div>
                <div className="school-profile-field school-profile-field-full">
                  <label>{t('business.schoolDashboard.profileForm.address')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.addressPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.municipality')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.municipalityPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.district')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.districtPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.wilaya')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.wilayaPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.phone')}</label>
                  <input type="tel" placeholder={t('business.schoolDashboard.profileForm.phonePlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.fax')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.faxPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.educationType')}</label>
                  <select>
                    <option value="">{t('business.schoolDashboard.profileForm.select')}</option>
                    <option value="external">{t('business.schoolDashboard.profileForm.external')}</option>
                    <option value="internal">{t('business.schoolDashboard.profileForm.internal')}</option>
                  </select>
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.director')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.directorPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.supervisor')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.supervisorPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.nationalId')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.nationalIdPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.academicYear')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.academicYearPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.numberOfRooms')}</label>
                  <input type="number" min="0" placeholder={t('business.schoolDashboard.profileForm.numberOfRoomsPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.educationalLevel')}</label>
                  <input type="text" placeholder={t('business.schoolDashboard.profileForm.educationalLevelPlaceholder')} />
                </div>
                <div className="school-profile-field">
                  <label>{t('business.schoolDashboard.profileForm.numberOfInspectors')}</label>
                  <input type="number" min="0" placeholder={t('business.schoolDashboard.profileForm.numberOfInspectorsPlaceholder')} />
                </div>
              </div>
              <button type="submit" className="school-profile-submit">{t('business.schoolDashboard.profileForm.save')}</button>
            </form>
          </div>
        )}

        {view === 'maintenance' && (
          <div className="school-dashboard-section">
            <p className="school-dashboard-placeholder">Infrastructure logs, IT and equipment management, classroom maintenance requests, notifications for pending tasks. This section will be implemented next.</p>
          </div>
        )}

        {view === 'settings' && (
          <div className="school-dashboard-section">
            <p className="school-dashboard-placeholder">Manage roles and permissions, school info and contact details, configure notifications and alerts, dashboard preferences. This section will be implemented next.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AccreditedSchoolDashboard;
